'use client';

import readHubMessage from '@/features/hub/shared/readHubMessage';
import { clientFetch } from '@/lib/api/clientFetch';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

function formatRequestTime(value, locale, fallbackLabel = '') {
  if (!value) {
    return fallbackLabel;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return fallbackLabel;
  }

  try {
    return new Intl.DateTimeFormat(locale || 'en', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  } catch {
    return date.toLocaleString();
  }
}

export default function HubMeetupModerationPanel({
  meetup,
  currentUser,
  locale,
  messages = {},
}) {
  const isAdmin = ['admin', 'superadmin'].includes(currentUser?.role || '');
  const isHost = Boolean(
    currentUser?.userNumber &&
    meetup?.hostUserNumber &&
    currentUser.userNumber === meetup.hostUserNumber,
  );
  const canModerate = Boolean(currentUser && (isHost || isAdmin));

  const router = useRouter();
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(canModerate);
  const [errorMessage, setErrorMessage] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [activeRequestKey, setActiveRequestKey] = useState('');
  const msg = (path, fallback = '', replacements = {}) =>
    readHubMessage(messages, path, fallback, replacements);

  useEffect(() => {
    let isMounted = true;

    const loadRequests = async () => {
      if (!canModerate) {
        setRequests([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setErrorMessage('');

      try {
        const response = await clientFetch(
          `/api/hub/meetups/${meetup.meetupNumber}/rsvp-requests`,
        );

        if (!isMounted) {
          return;
        }

        setRequests(Array.isArray(response?.requests) ? response.requests : []);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setErrorMessage(
          error?.data?.message ||
            error?.message ||
            msg(
              'meetup.moderation.errors.loadRequests',
              'Unable to load RSVP requests.',
            ),
        );
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadRequests();

    return () => {
      isMounted = false;
    };
  }, [canModerate, meetup.meetupNumber]);

  if (!canModerate) {
    return null;
  }

  const updateRequestStatus = async (userNumber, status) => {
    const requestKey = `${userNumber}:${status}`;
    const targetRequest = requests.find(
      (request) => request.userNumber === userNumber,
    );
    const requestLabel =
      targetRequest?.user?.hubDisplayName ||
      targetRequest?.user?.name ||
      userNumber;

    setErrorMessage('');
    setStatusMessage('');
    setActiveRequestKey(requestKey);

    try {
      await clientFetch(
        `/api/hub/meetups/${meetup.meetupNumber}/rsvp-requests/${encodeURIComponent(userNumber)}`,
        {
          method: 'PATCH',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({ status }),
        },
      );

      setRequests((currentRequests) =>
        currentRequests.filter((request) => request.userNumber !== userNumber),
      );
      setStatusMessage(
        msg(
          status === 'approved'
            ? 'meetup.moderation.approvedToast'
            : 'meetup.moderation.rejectedToast',
          status === 'approved'
            ? 'Approved RSVP request from {{name}}.'
            : 'Rejected RSVP request from {{name}}.',
          { name: requestLabel },
        ),
      );
      router.refresh();
    } catch (error) {
      setErrorMessage(
        error?.data?.message ||
          error?.message ||
          msg(
            'meetup.moderation.errors.updateStatus',
            'Unable to update RSVP request status.',
          ),
      );
    } finally {
      setActiveRequestKey('');
    }
  };

  return (
    <section
      id="meetup-rsvp-requests"
      className="app-panel hub-action-panel hub-action-panel--meetups"
    >
      <div className="hub-action-panel__header">
        <div>
          <p className="hub-section__eyebrow hub-section__eyebrow--warning">
            {msg('meetup.moderation.eyebrow', 'Host Review')}
          </p>
          <h2>{msg('meetup.moderation.title', 'RSVP request review')}</h2>
        </div>
        <span className="hub-pill hub-pill--warning">
          {msg('meetup.moderation.pendingCount', '{{count}} pending', {
            count: requests.length,
          })}
        </span>
      </div>

      <p className="shop-panel__meta">
        {msg(
          'meetup.moderation.hint',
          'Review pending RSVP requests for this meetup directly from the detail page.',
        )}
      </p>

      {statusMessage ? (
        <div className="auth-message auth-message--success">
          {statusMessage}
        </div>
      ) : null}

      {errorMessage ? (
        <div className="auth-message auth-message--error">{errorMessage}</div>
      ) : null}

      {isLoading ? (
        <p className="shop-panel__meta">
          {msg('meetup.moderation.loading', 'Loading RSVP requests...')}
        </p>
      ) : requests.length > 0 ? (
        <div className="hub-request-list">
          {requests.map((request) => {
            const requestLabel =
              request.user?.hubDisplayName ||
              request.user?.name ||
              request.userNumber;

            return (
              <article
                key={request.userNumber}
                className="hub-request-card surface-card"
              >
                <div className="hub-request-card__header">
                  <div className="hub-request-card__identity">
                    <h3>{requestLabel}</h3>
                    <p className="product-card__meta">
                      {request.userNumber} •{' '}
                      {msg('meetup.moderation.requestedLabel', 'Requested')}{' '}
                      {formatRequestTime(
                        request.createdAt,
                        locale,
                        msg('meetup.moderation.unknownTime', 'Unknown time'),
                      )}
                    </p>
                  </div>
                  <span className="hub-pill hub-pill--warning">
                    {msg('meetup.moderation.pendingBadge', 'pending')}
                  </span>
                </div>

                <p className="shop-panel__meta">
                  {msg(
                    'meetup.moderation.requestHint',
                    'Approve this RSVP to unlock the meetup join information for the attendee, or reject it to keep the request closed.',
                  )}
                </p>

                <div className="auth-form__actions">
                  <button
                    type="button"
                    className="app-button app-button--primary app-button--compact"
                    onClick={() =>
                      updateRequestStatus(request.userNumber, 'approved')
                    }
                    disabled={Boolean(activeRequestKey)}
                  >
                    {activeRequestKey === `${request.userNumber}:approved`
                      ? msg('meetup.moderation.approveLoading', 'Approving...')
                      : msg('meetup.approve', 'Approve')}
                  </button>
                  <button
                    type="button"
                    className="app-button app-button--ghost app-button--compact"
                    onClick={() =>
                      updateRequestStatus(request.userNumber, 'rejected')
                    }
                    disabled={Boolean(activeRequestKey)}
                  >
                    {activeRequestKey === `${request.userNumber}:rejected`
                      ? msg('meetup.moderation.rejectLoading', 'Rejecting...')
                      : msg('meetup.reject', 'Reject')}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <p className="shop-panel__meta">
          {msg('meetup.rsvpRequestsEmpty', 'No pending join requests.')}
        </p>
      )}
    </section>
  );
}
